/**
 * PublicLeagueHomePage — the league landing page (/l/:slug)
 * ─────────────────────────────────────────────────────────────────
 * REDESIGNED in the public pages overhaul P3 (2026-06-09); supersedes
 * the v2.5.132 layout. Modern hero + the manager's public blog.
 *
 * Above the fold: themed hero (gradient from the manager's accent
 * content block) with league name, tagline, season chips, Register
 * CTA, and a "find your team" card (instant filter → team page).
 *
 * Below: League News (league_posts via P2 home payload), Upcoming
 * Games, a Standings snapshot, About (markdown content block), and
 * quick links. Blocks render only when they have content.
 */

import { useEffect, useMemo, useState } from 'react';
import { Link, useOutletContext, useParams, useNavigate } from 'react-router-dom';
import publicApi from '../../services/publicApi.js';
import { renderMarkdown } from '../../utils/tinyMarkdown.jsx';
import {
  container, Section, Card, Chip, CtaButton, EmptyState,
  formatDateNice, formatTimeNice,
  INK, MUTED, LINE, SURFACE,
} from './pubUi.jsx';

export default function PublicLeagueHomePage() {
  const { slug } = useParams();
  const {
    league, currentSeason, contentBlocks, upcomingGames,
    latestPosts, registrationOpen, accent,
  } = useOutletContext();

  useEffect(() => {
    if (league?.name) document.title = `${league.name} — Home`;
  }, [league?.name]);

  // Standings snapshot for the homepage (top of each division).
  const [standings, setStandings] = useState(null);
  useEffect(() => {
    if (!currentSeason?.id) return;
    let cancelled = false;
    publicApi.get(`/leagues/${encodeURIComponent(slug)}/seasons/${currentSeason.id}/standings`)
      .then(({ data }) => { if (!cancelled) setStandings(data); })
      .catch(() => { if (!cancelled) setStandings(null); });
    return () => { cancelled = true; };
  }, [slug, currentSeason?.id]);

  const tagline = contentBlocks.hero_tagline
    || `${league.sport === 'hockey' ? 'Hockey' : 'Sports'} · run on Game Day`;

  return (
    <div>
      {/* ── Hero ──────────────────────────────────────────────── */}
      <div style={{
        background: `linear-gradient(135deg, ${accent} 0%, #0b1220 90%)`,
        color: '#fff',
        padding: '64px 0 56px',
        marginBottom: 40,
      }}>
        <div style={{ ...container, display: 'flex', gap: 40, alignItems: 'center', flexWrap: 'wrap' }}>
          <div style={{ flex: '1 1 460px', minWidth: 0 }}>
            <div style={{ fontSize: 12, fontWeight: 800, letterSpacing: '0.18em', textTransform: 'uppercase', opacity: 0.85, marginBottom: 10 }}>
              {currentSeason ? currentSeason.name : 'Official league site'}
            </div>
            <h1 style={{ fontSize: 'clamp(32px, 5vw, 52px)', fontWeight: 900, letterSpacing: '-0.03em', margin: '0 0 14px', lineHeight: 1.05 }}>
              {league.name}
            </h1>
            <p style={{ fontSize: 18, opacity: 0.92, margin: '0 0 26px', maxWidth: 560 }}>
              {tagline}
            </p>

            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center' }}>
              {registrationOpen && (
                <CtaButton to={`/l/${slug}/register`} accent="#ffffff">
                  {contentBlocks.hero_cta_text || 'Register now'} →
                </CtaButton>
              )}
              <CtaButton to={`/l/${slug}/schedule`} accent="#ffffff" ghost>
                View schedule
              </CtaButton>
            </div>

            <div style={{ display: 'flex', gap: 10, marginTop: 26, flexWrap: 'wrap' }}>
              {currentSeason?.team_count > 0 && (
                <HeroChip>{currentSeason.team_count} teams</HeroChip>
              )}
              {currentSeason?.status === 'active' && <HeroChip>Season live</HeroChip>}
              <HeroChip>{(league.sport || 'hockey').replace(/^./, c => c.toUpperCase())}</HeroChip>
            </div>
          </div>

          {/* Hero team-finder card */}
          <div style={{ flex: '0 1 360px', width: '100%' }}>
            <HeroTeamFinder slug={slug} accent={accent} />
          </div>
        </div>
      </div>

      <div style={container}>
        {/* ── League news ───────────────────────────────────────── */}
        <Section
          eyebrow="From the league office"
          title="League news"
          action={
            <Link to={`/l/${slug}/news`} style={{ color: accent, fontWeight: 800, textDecoration: 'none', fontSize: 14 }}>
              All news →
            </Link>
          }>
          {latestPosts.length === 0 ? (
            <EmptyState icon="📰">No news posted yet — check back soon.</EmptyState>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 18 }}>
              {latestPosts.map(p => <NewsCard key={p.id} slug={slug} post={p} accent={accent} />)}
            </div>
          )}
        </Section>

        {/* ── Upcoming + standings snapshot ─────────────────────── */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: 24, marginBottom: 44 }}>
          <Section
            eyebrow="On the ice next"
            title="Upcoming games"
            style={{ marginBottom: 0 }}
            action={
              <Link to={`/l/${slug}/schedule`} style={{ color: accent, fontWeight: 800, textDecoration: 'none', fontSize: 14 }}>
                Full schedule →
              </Link>
            }>
            {upcomingGames.length === 0 ? (
              <EmptyState icon="🗓">No upcoming games scheduled.</EmptyState>
            ) : (
              <Card>
                {upcomingGames.map((g, i) => (
                  <Link key={g.id} to={`/l/${slug}/game/${g.id}`} style={{
                    display: 'flex', alignItems: 'center', gap: 14, padding: '13px 18px',
                    borderBottom: i < upcomingGames.length - 1 ? `1px solid ${LINE}` : 'none',
                    color: INK, textDecoration: 'none',
                  }}>
                    <div style={{ width: 84, flexShrink: 0 }}>
                      <div style={{ fontWeight: 800, fontSize: 13 }}>{formatDateNice(g.game_date)}</div>
                      <div style={{ fontSize: 12, color: MUTED }}>{formatTimeNice(g.game_time)}</div>
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontWeight: 700, fontSize: 14 }}>
                        {g.home_team_name} <span style={{ color: MUTED, fontWeight: 500 }}>vs</span> {g.away_team_name}
                      </div>
                      {g.venue_name && <div style={{ fontSize: 12, color: MUTED }}>{g.venue_name}</div>}
                    </div>
                    <span style={{ color: accent, fontWeight: 800 }}>→</span>
                  </Link>
                ))}
              </Card>
            )}
          </Section>

          <Section
            eyebrow="How it stands"
            title="Standings"
            style={{ marginBottom: 0 }}
            action={
              <Link to={`/l/${slug}/standings`} style={{ color: accent, fontWeight: 800, textDecoration: 'none', fontSize: 14 }}>
                Full standings →
              </Link>
            }>
            {!standings?.divisions?.length ? (
              <EmptyState icon="📊">Standings appear once games are played.</EmptyState>
            ) : (
              <Card style={{ overflow: 'hidden' }}>
                {standings.divisions.map(d => (
                  <div key={d.division_id || 'all'}>
                    {d.division_name && (
                      <div style={{ padding: '10px 18px 4px', fontSize: 11, fontWeight: 800, letterSpacing: '0.1em', textTransform: 'uppercase', color: MUTED }}>
                        {d.division_name}
                      </div>
                    )}
                    {d.teams.slice(0, 4).map((t, i) => (
                      <Link key={t.team_id} to={`/l/${slug}/team/${t.team_id}`} style={{
                        display: 'flex', alignItems: 'center', gap: 12, padding: '9px 18px',
                        color: INK, textDecoration: 'none',
                        borderBottom: `1px solid ${LINE}`,
                      }}>
                        <span style={{ width: 20, fontWeight: 800, color: i === 0 ? accent : MUTED }}>{i + 1}</span>
                        <span style={{ flex: 1, fontWeight: 700, fontSize: 14, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{t.team_name}</span>
                        <span style={{ fontSize: 12, color: MUTED }}>{t.w}-{t.l}{t.t ? `-${t.t}` : ''}</span>
                        <span style={{ fontWeight: 800, fontSize: 14, width: 44, textAlign: 'right' }}>{t.pts} <span style={{ fontSize: 10, color: MUTED }}>PTS</span></span>
                      </Link>
                    ))}
                  </div>
                ))}
              </Card>
            )}
          </Section>
        </div>

        {/* ── About ─────────────────────────────────────────────── */}
        {contentBlocks.about_markdown && (
          <Section eyebrow="About" title={`About ${league.name}`}>
            <Card style={{ padding: '26px 28px', maxWidth: 820 }}>
              {renderMarkdown(contentBlocks.about_markdown)}
            </Card>
          </Section>
        )}

        {/* ── Quick links ───────────────────────────────────────── */}
        <Section eyebrow="Explore" title="Around the league">
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 16 }}>
            {[
              ['🏒', 'Teams', 'Every club, every division', `/l/${slug}/teams`],
              ['📅', 'Schedule & results', 'Published games and final scores', `/l/${slug}/schedule`],
              ['🏆', 'Playoffs', 'Brackets and championship runs', `/l/${slug}/playoffs`],
              ['👤', 'Coaches', 'The people behind the benches', `/l/${slug}/coaches`],
              ['📈', 'Stat leaders', 'Goals, assists, points', `/l/${slug}/leaders`],
              ...(registrationOpen ? [['📝', 'Registration', 'Sign up to play', `/l/${slug}/register`]] : []),
            ].map(([icon, title, sub, to]) => (
              <Link key={to} to={to} style={{ textDecoration: 'none', color: INK }}>
                <Card hover style={{ padding: '20px 20px 18px', height: '100%' }}>
                  <div style={{ fontSize: 26, marginBottom: 10 }}>{icon}</div>
                  <div style={{ fontWeight: 800, fontSize: 16, marginBottom: 3 }}>{title}</div>
                  <div style={{ fontSize: 13, color: MUTED }}>{sub}</div>
                </Card>
              </Link>
            ))}
          </div>
        </Section>
      </div>
    </div>
  );
}

function HeroChip({ children }) {
  return (
    <span style={{
      padding: '6px 14px', borderRadius: 999,
      background: 'rgba(255,255,255,0.14)', border: '1px solid rgba(255,255,255,0.25)',
      fontSize: 12.5, fontWeight: 800, letterSpacing: '0.04em',
    }}>
      {children}
    </span>
  );
}

/** The hero's "find your team" card — instant filter + jump. */
function HeroTeamFinder({ slug, accent }) {
  const navigate = useNavigate();
  const [teams, setTeams] = useState(null);
  const [q, setQ] = useState('');

  useEffect(() => {
    let cancelled = false;
    publicApi.get(`/leagues/${encodeURIComponent(slug)}/teams`)
      .then(({ data }) => { if (!cancelled) setTeams(data?.teams || []); })
      .catch(() => { if (!cancelled) setTeams([]); });
    return () => { cancelled = true; };
  }, [slug]);

  const matches = useMemo(() => {
    if (!teams) return [];
    if (!q.trim()) return teams.slice(0, 5);
    const needle = q.trim().toLowerCase();
    return teams.filter(t => t.team_name.toLowerCase().includes(needle)).slice(0, 5);
  }, [teams, q]);

  return (
    <div style={{
      background: 'rgba(255,255,255,0.97)', borderRadius: 18, padding: 20,
      boxShadow: '0 18px 60px rgba(0,0,0,0.35)', color: INK,
    }}>
      <div style={{ fontWeight: 800, fontSize: 16, marginBottom: 4 }}>Find your team</div>
      <div style={{ fontSize: 13, color: MUTED, marginBottom: 12 }}>
        Schedules, results, news and a calendar feed — one page per team.
      </div>
      <input
        type="search"
        value={q}
        onChange={(e) => setQ(e.target.value)}
        onKeyDown={(e) => { if (e.key === 'Enter' && matches[0]) navigate(`/l/${slug}/team/${matches[0].team_id}`); }}
        placeholder="Type a team name…"
        style={{
          width: '100%', padding: '12px 14px', borderRadius: 12,
          border: `1px solid ${LINE}`, background: SURFACE,
          fontSize: 15, outline: 'none', marginBottom: 10,
          boxSizing: 'border-box',
        }}
      />
      <div>
        {teams === null && <div style={{ fontSize: 13, color: MUTED, padding: '8px 2px' }}>Loading teams…</div>}
        {teams !== null && matches.length === 0 && (
          <div style={{ fontSize: 13, color: MUTED, padding: '8px 2px' }}>No team matches.</div>
        )}
        {matches.map(t => (
          <button key={t.team_id} type="button"
            onClick={() => navigate(`/l/${slug}/team/${t.team_id}`)}
            style={{
              display: 'flex', alignItems: 'center', gap: 10, width: '100%',
              padding: '9px 10px', borderRadius: 10, border: 'none',
              background: 'transparent', cursor: 'pointer', textAlign: 'left',
              fontSize: 14, fontWeight: 700, color: INK,
            }}
            onMouseEnter={(e) => { e.currentTarget.style.background = SURFACE; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}>
            <span style={{ color: accent }}>›</span>
            <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{t.team_name}</span>
            {t.division_name && <span style={{ fontSize: 11, color: MUTED, fontWeight: 600 }}>{t.division_name}</span>}
          </button>
        ))}
      </div>
      <Link to={`/l/${slug}/teams`} style={{ display: 'inline-block', marginTop: 8, color: accent, fontWeight: 800, fontSize: 13, textDecoration: 'none' }}>
        Browse all teams →
      </Link>
    </div>
  );
}

/** Compact news card shared by the homepage + news feed grids. */
export function NewsCard({ slug, post, accent }) {
  return (
    <Link to={`/l/${slug}/news/${post.id}`} style={{ textDecoration: 'none', color: INK }}>
      <Card hover style={{ overflow: 'hidden', height: '100%', display: 'flex', flexDirection: 'column' }}>
        {post.image_url ? (
          <img src={post.image_url} alt="" style={{ width: '100%', height: 160, objectFit: 'cover', display: 'block' }} />
        ) : (
          <div style={{ height: 8, background: accent }} />
        )}
        <div style={{ padding: '16px 18px 18px', flex: 1, display: 'flex', flexDirection: 'column' }}>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 8, flexWrap: 'wrap' }}>
            {post.pinned && <Chip color="#9a6700" bg="#fff3d1">Pinned</Chip>}
            {post.team_name && <Chip>{post.team_name}</Chip>}
            <span style={{ fontSize: 12, color: MUTED }}>{formatDateNice(post.created_at)}</span>
          </div>
          <div style={{ fontWeight: 800, fontSize: 17, lineHeight: 1.3, marginBottom: 6 }}>{post.title}</div>
          {post.excerpt && (
            <p style={{ margin: 0, fontSize: 13.5, color: MUTED, lineHeight: 1.55, flex: 1 }}>
              {post.excerpt}{post.excerpt.length >= 220 ? '…' : ''}
            </p>
          )}
          <div style={{ marginTop: 12, color: accent, fontWeight: 800, fontSize: 13 }}>Read more →</div>
        </div>
      </Card>
    </Link>
  );
}
